/**
 * Tiny shared loading/error/empty wrapper for pages backed by stores.
 * Visual is intentionally minimal so it doesn't compete with each page's
 * own design — pages use this only when no data is renderable yet.
 */
import React from 'react';

interface AsyncBoundaryProps {
  loading?: boolean;
  error?: string | null;
  empty?: boolean;
  emptyMessage?: string;
  onRetry?: () => void;
  children: React.ReactNode;
}

export const AsyncBoundary: React.FC<AsyncBoundaryProps> = ({
  loading,
  error,
  empty,
  emptyMessage = 'No records to display',
  onRetry,
  children,
}) => {
  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#6B7280' }}>
        <div
          style={{
            display: 'inline-block',
            width: 24,
            height: 24,
            border: '3px solid #E5E7EB',
            borderTopColor: '#2563EB',
            borderRadius: '50%',
            animation: 'asyncSpin 0.7s linear infinite',
          }}
        />
        <p style={{ marginTop: 12, fontSize: 14 }}>Loading…</p>
        <style>{`@keyframes asyncSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: 24,
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          color: '#B91C1C',
          borderRadius: 8,
          margin: 16,
        }}
      >
        <strong>Couldn’t load data.</strong> <span style={{ opacity: 0.8 }}>{error}</span>
        {onRetry && (
          <button
            onClick={onRetry}
            style={{
              marginLeft: 12,
              padding: '4px 10px',
              border: '1px solid #B91C1C',
              borderRadius: 4,
              background: 'transparent',
              color: '#B91C1C',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (empty) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#6B7280' }}>
        <p style={{ fontSize: 14 }}>{emptyMessage}</p>
      </div>
    );
  }

  return <>{children}</>;
};

export default AsyncBoundary;
